import { create } from 'zustand';
import { templatesApi } from '../api';

const useTemplateStore = create((set, get) => ({
  templates: [],
  currentTemplate: null,
  drafts: [],
  categories: [],
  languages: [],
  isLoading: false,
  error: null,

  setTemplates: (templates) => set({ templates }),

  setCurrentTemplate: (template) => set({ currentTemplate: template }),

  fetchTemplates: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await templatesApi.getAll(params);
      set({ templates: response.data.data || response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch templates', isLoading: false });
      throw error;
    }
  },

  fetchTemplateById: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await templatesApi.getById(id);
      set({ currentTemplate: response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch template', isLoading: false });
      throw error;
    }
  },

  createTemplate: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await templatesApi.create(data);
      set((state) => ({
        templates: [...state.templates, response.data],
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to create template', isLoading: false });
      throw error;
    }
  },

  updateTemplate: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await templatesApi.update(id, data);
      set((state) => ({
        templates: state.templates.map(t => t.id === id ? response.data : t),
        currentTemplate: state.currentTemplate?.id === id ? response.data : state.currentTemplate,
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to update template', isLoading: false });
      throw error;
    }
  },

  deleteTemplate: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await templatesApi.delete(id);
      set((state) => ({
        templates: state.templates.filter(t => t.id !== id),
        currentTemplate: state.currentTemplate?.id === id ? null : state.currentTemplate,
        isLoading: false
      }));
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to delete template', isLoading: false });
      throw error;
    }
  },

  submitTemplate: async (id) => {
    set({ isLoading: true, error: null });
    try {
      const response = await templatesApi.submit(id);
      set((state) => ({
        templates: state.templates.map(t => t.id === id ? { ...t, status: 'pending' } : t),
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to submit template', isLoading: false });
      throw error;
    }
  },

  fetchDrafts: async (params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await templatesApi.getDrafts(params);
      set({ drafts: response.data.data || response.data, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to fetch drafts', isLoading: false });
      throw error;
    }
  },

  saveDraft: async (data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await templatesApi.saveDraft(data);
      set((state) => ({
        drafts: [...state.drafts, response.data],
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to save draft', isLoading: false });
      throw error;
    }
  },

  updateDraft: async (id, data) => {
    set({ isLoading: true, error: null });
    try {
      const response = await templatesApi.updateDraft(id, data);
      set((state) => ({
        drafts: state.drafts.map(d => d.id === id ? response.data : d),
        isLoading: false
      }));
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to update draft', isLoading: false });
      throw error;
    }
  },

  deleteDraft: async (id) => {
    set({ isLoading: true, error: null });
    try {
      await templatesApi.deleteDraft(id);
      set((state) => ({
        drafts: state.drafts.filter(d => d.id !== id),
        isLoading: false
      }));
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to delete draft', isLoading: false });
      throw error;
    }
  },

  fetchCategories: async () => {
    try {
      const response = await templatesApi.getCategories();
      set({ categories: response.data });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      throw error;
    }
  },

  fetchLanguages: async () => {
    try {
      const response = await templatesApi.getLanguages();
      set({ languages: response.data });
      return response.data;
    } catch (error) {
      console.error('Failed to fetch languages:', error);
      throw error;
    }
  },

  syncTemplates: async () => {
    set({ isLoading: true, error: null });
    try {
      const response = await templatesApi.sync();
      set({ isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || 'Failed to sync templates', isLoading: false });
      throw error;
    }
  },

  clearError: () => set({ error: null }),

  reset: () => set({
    templates: [],
    currentTemplate: null,
    drafts: [],
    categories: [],
    languages: [],
    isLoading: false,
    error: null
  }),
}));

export default useTemplateStore;
